/**
 * import-followups — pure helpers that turn a successful client-side import (CSV
 * contacts, transaction CSV, tasks) into result-aware follow-up chips (#2014).
 *
 * After "Imported N <things>." lands, the chat used to go silent. These helpers
 * build 3-5 QuickAction chips that reference the ACTUAL imported count (and the
 * most common tag / category present in the imported rows) so the user has an
 * obvious next step. Chips carry only { label, message } (NO `kind`), so
 * MessageList's onSelect falls through to `sendMessage(message)` — see
 * MessageList.tsx ~L999.
 *
 * This is the DETERMINISTIC client-side half of #2014. The agent-side
 * `suggestedFollowUps` tool-result envelope (object_search / bulk_create_contacts)
 * and the LLM-prompt fallback for unstructured parses (PDF/image/audio — see the
 * Action-chips rule in apps/x-agent/workspace/AGENTS.md) cover the rest.
 * Pure: no React, no I/O.
 */

import type { QuickAction } from '@/features/chat/hooks/useChat.types'

/** Import object types that get a deterministic client-side follow-up recipe. */
export type ImportObjectType = 'contact' | 'transaction' | 'task'

export interface BuildImportFollowUpsInput {
  /** Number of rows that were successfully created. */
  imported: number
  /** Number of rows that failed (currently informational only). */
  failed: number
  /** Which import recipe to use. */
  objectType: ImportObjectType
  /**
   * Most common label across the imported rows, if any — drives the flagship
   * chip. For `contact` this is the top tag; for `transaction` the top spend
   * category; ignored for `task`.
   */
  topTag?: string | null
}

/**
 * Build 3-5 result-aware follow-up chips for a successful import. Dispatches on
 * `objectType`. Returns [] when nothing was imported (`imported <= 0`).
 */
export function buildImportFollowUps(input: BuildImportFollowUpsInput): QuickAction[] {
  const { imported, objectType, topTag } = input
  if (imported <= 0) return []

  if (objectType === 'transaction') return buildTransactionFollowUps(imported, topTag)
  if (objectType === 'task') return buildTaskFollowUps(imported)
  return buildContactFollowUps(imported, topTag)
}

/** Contacts: tag / dedupe / segment / export — the flagship #2014 flow. */
function buildContactFollowUps(imported: number, topTag?: string | null): QuickAction[] {
  // Grammatically agree the noun with the count so a single-row import doesn't
  // read "the 1 contacts" — mirrors the summary toast in ChatCsvImportModal.
  const noun = imported === 1 ? 'contact' : 'contacts'

  const tagChip: QuickAction = topTag
    ? {
        label: `Tag all ${imported} with "${topTag}"`,
        message: `Tag all ${imported} ${noun} I just imported with the tag "${topTag}".`,
      }
    : {
        label: `Tag the ${imported} imported ${noun}`,
        message: `Tag the ${imported} ${noun} I just imported.`,
      }

  return [
    tagChip,
    {
      label: 'Find duplicates',
      message: `Find duplicates among the ${imported} ${noun} I just imported and against my existing contacts.`,
    },
    {
      label: 'Group by company',
      message: `Group the ${imported} imported ${noun} by company/organization.`,
    },
    {
      label: 'Export this segment',
      message: `Export the ${imported} ${noun} I just imported to a new list.`,
    },
  ]
}

/** Transactions: sum / group-by-category / chart-spend / flag-outliers (#2014 table). */
function buildTransactionFollowUps(imported: number, topCategory?: string | null): QuickAction[] {
  const noun = imported === 1 ? 'transaction' : 'transactions'
  const flagshipChip: QuickAction = topCategory
    ? {
        label: `Total spend on "${topCategory}"`,
        message: `Sum the total amount across the ${imported} ${noun} I just imported, broken out by category — start with "${topCategory}".`,
      }
    : {
        label: `Sum the ${imported} ${noun}`,
        message: `Sum the total amount across the ${imported} ${noun} I just imported.`,
      }
  return [
    flagshipChip,
    {
      label: 'Group by category',
      message: `Group the ${imported} ${noun} I just imported by category and total each group.`,
    },
    {
      label: 'Chart spend over time',
      message: `Chart spend over time for the ${imported} ${noun} I just imported.`,
    },
    {
      label: 'Flag outliers',
      message: `Flag any unusually large or outlier amounts among the ${imported} ${noun} I just imported.`,
    },
  ]
}

/** Tasks: prioritize / due-soon / group-by-status / export. */
function buildTaskFollowUps(imported: number): QuickAction[] {
  const noun = imported === 1 ? 'task' : 'tasks'
  return [
    {
      label: `Prioritize the ${imported} ${noun}`,
      message: `Prioritize the ${imported} ${noun} I just imported and tell me what to do first.`,
    },
    {
      label: 'What is due soon?',
      message: `Of the ${imported} ${noun} I just imported, which are due soon or overdue?`,
    },
    {
      label: 'Group by status',
      message: `Group the ${imported} ${noun} I just imported by status.`,
    },
    {
      label: 'Export this segment',
      message: `Export the ${imported} ${noun} I just imported to a new list.`,
    },
  ]
}

/**
 * Lightweight column-shape detection: classify a parsed CSV/list as a
 * transaction sheet vs a contact sheet by its header/field names. Used to pick
 * the right follow-up recipe when an import surface handles more than one shape.
 * Transaction signal = an amount-like column AND (a date-like OR category-like)
 * column; contact signal = email/phone/name columns. Returns the best guess, or
 * the supplied `fallback` when neither dominates. Pure, case-insensitive.
 */
export function detectImportObjectType(
  columns: string[],
  fallback: ImportObjectType = 'contact',
): ImportObjectType {
  const norm = columns.map((c) => (typeof c === 'string' ? c.trim().toLowerCase() : ''))
  const has = (...needles: string[]) =>
    norm.some((col) => needles.some((n) => col === n || col.includes(n)))

  const hasAmount = has('amount', 'price', 'total', 'cost', 'debit', 'credit', 'balance')
  const hasDate = has('date', 'posted', 'transaction date')
  const hasCategory = has('category', 'merchant', 'payee', 'description', 'memo')
  const hasContact = has('email', 'phone', 'first name', 'last name', 'full name')

  const txnScore = (hasAmount ? 2 : 0) + (hasDate ? 1 : 0) + (hasCategory ? 1 : 0)
  // An amount column plus a date OR category is a confident transaction shape.
  if (hasAmount && (hasDate || hasCategory) && txnScore > (hasContact ? 2 : 0)) {
    return 'transaction'
  }
  if (hasContact) return 'contact'
  return fallback
}

/**
 * Append `incoming` chips to `existing`, deduped by label (existing wins). Pure
 * reducer behind useChat's imperative `addQuickActions` — kept here so the
 * dedupe contract is unit-testable without rendering the SSE-backed hook.
 */
export function appendQuickActions(
  existing: QuickAction[],
  incoming: QuickAction[],
): QuickAction[] {
  const seen = new Set(existing.map((a) => a.label))
  const additions: QuickAction[] = []
  for (const action of incoming) {
    if (seen.has(action.label)) continue
    seen.add(action.label)
    additions.push(action)
  }
  return additions.length === 0 ? existing : [...existing, ...additions]
}

/**
 * Tally tag frequency across the imported rows and return the single most common
 * tag, or null when no row carries a tag. Ties resolve to the first-seen tag
 * (deterministic, insertion order).
 */
export function pickTopTag(contacts: { tags?: string[] }[]): string | null {
  const counts = new Map<string, number>()
  for (const contact of contacts) {
    const tags = Array.isArray(contact.tags) ? contact.tags : []
    for (const raw of tags) {
      const tag = typeof raw === 'string' ? raw.trim() : ''
      if (!tag) continue
      counts.set(tag, (counts.get(tag) ?? 0) + 1)
    }
  }

  let topTag: string | null = null
  let topCount = 0
  // Map iteration order is insertion order, so the first tag to reach a given
  // count wins ties (first-seen).
  for (const [tag, count] of counts) {
    if (count > topCount) {
      topTag = tag
      topCount = count
    }
  }
  return topTag
}
